import type { ReactNode } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl, type MessageDescriptor } from 'react-intl'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import type LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'

export type LuxPlusBenefitIcon = typeof LineBedIcon

export interface LuxPlusBenefitItem {
  icon: LuxPlusBenefitIcon
  title: MessageDescriptor
  body?: MessageDescriptor
}

const Box = styled.div`
  border: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${(props) => props.theme.borderRadius.L};
  padding: ${rem(20)};
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
`

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(12)};
`

const IconCell = styled.div`
  flex-shrink: 0;
  display: flex;
`

/**
 * Bordered LuxPlus+ benefits card: icon + bold headline + optional subtext per row, with an optional
 * trailing slot (e.g. a "Plus so much more" link). Shared by the member-benefits list and the
 * reactivation success screen.
 */
function LuxPlusBenefitsCard({ items, footer }: { items: Array<LuxPlusBenefitItem>; footer?: ReactNode }) {
  const intl = useIntl()

  return (
    <Box>
      {items.map(({ icon: Icon, title, body }) => (
        <Row key={title.id}>
          <IconCell>
            <Icon size="S" colour="lux-plus" />
          </IconCell>
          <Group direction="vertical" gap={2}>
            <BodyText variant="medium" weight="bold">{intl.formatMessage(title)}</BodyText>
            {body && <BodyText variant="medium" colour="light-foreground-alt">{intl.formatMessage(body)}</BodyText>}
          </Group>
        </Row>
      ))}
      {footer}
    </Box>
  )
}

export default LuxPlusBenefitsCard
